import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for fetching event-related data
 */
export const useEventData = (eventId, industryId = null) => {
  const [eventDetails, setEventDetails] = useState(null);
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch event details
   */
  const fetchEventDetails = useCallback(async (eventIdParam) => {
    const idToUse = eventIdParam || eventId;
    if (!idToUse) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetEvent($id: ID!) {
            getEvent(id: $id) {
              id
              name {
                language
                text
              }
              type
              boothBooking
              status
              date
              startDate
              endDate
              startTime
              endTime
              daySchedules {
                day
                date
                exhibitorStart
                exhibitorEnd
                visitorStart
                visitorEnd
              }
              lectureRooms
              lectureRegistrationStartDate
              lectureRegistrationEndDate
              lectureEditEndDate
              lectureDocumentsEndDate
              lecturePresentationEndDate
              lectureRecordingCorrectionEndDate
              availableLectureTypes
              location {
                name {
                  language
                  text
                }
                address {
                  street
                  streetNumber
                  city
                  zip
                  countryCode
                }
                coordinates {
                  lat
                  lng
                }
              }
              industries {
                items {
                  industry {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
              }
              suggestedInvitationCode
              exhibitorLectures
            }
          }
        `,
        variables: { id: idToUse }
      });
      
      const event = response.data.getEvent;
      setEventDetails(event);
      return event;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-event-details',
        consoleMessage: 'Error fetching event details:',
        context: {
          eventId: idToUse
        }
      });

      setError(error.message || 'Failed to fetch event details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  /**
   * Fetch all topics with their groups
   */
  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build filter based on industryId
      const filter = industryId ? { industryId: { eq: industryId } } : undefined;

      const response = await client.graphql({
        query: /* GraphQL */ `
          query ListTopicGroups(
            $filter: ModelTopicGroupFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                color
                sortKey
                name {
                  language
                  text
                }
                topics {
                  items {
                    id
                    name {
                      language
                      text
                    }
                    color
                    sortKey
                    topicGroupTopicsId
                  }
                }
              }
              nextToken
            }
          }
        `,
        variables: {
          filter,
          limit: 1000
        }
      });

      const allTopicGroups = response.data.listTopicGroups.items || [];

      // Sort topic groups by sortKey
      const sortedTopicGroups = allTopicGroups
        .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

      setTopicGroups(sortedTopicGroups);

      // Flatten topics from all groups
      const topicsFlat = sortedTopicGroups
        .map((topicGroup) => topicGroup.topics.items)
        .flat()
        .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

      setTopics(topicsFlat);

      return { topicGroups: sortedTopicGroups, topics: topicsFlat };
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-topics',
        consoleMessage: 'Error fetching topics:',
        context: {
          industryId
        }
      });

      setError(error.message || 'Failed to fetch topics');
      return { topicGroups: [], topics: [] };
    } finally {
      setIsLoading(false);
    }
  }, [industryId]);

  /**
   * Fetch topics for a specific event (may have event-specific filtering in the future)
   */
  const fetchEventTopics = useCallback(async (eventIdParam) => {
    // For now, just fetch all topics
    // In the future, this could filter based on event-specific configuration
    return fetchTopics();
  }, [fetchTopics]);

  /**
   * Get topic by ID
   */
  const getTopicById = useCallback((topicId) => {
    return topics.find(topic => topic.id === topicId);
  }, [topics]);

  /**
   * Get topics by IDs
   */
  const getTopicsByIds = useCallback((topicIds) => {
    if (!topicIds || !Array.isArray(topicIds)) return [];
    return topics.filter(topic => topicIds.includes(topic.id));
  }, [topics]);

  /**
   * Get topic group by ID
   */
  const getTopicGroupById = useCallback((groupId) => {
    return topicGroups.find(group => group.id === groupId);
  }, [topicGroups]);

  /**
   * Search topics by name
   */
  const searchTopics = useCallback((searchTerm, language = 'de') => {
    if (!searchTerm) return topics;
    
    const searchLower = searchTerm.toLowerCase();
    return topics.filter((topic) => {
      const topicName = topic.name?.find(n => n.language === language)?.text || '';
      return topicName.toLowerCase().includes(searchLower);
    });
  }, [topics]);

  /**
   * Get available lecture types for the event
   */
  const getAvailableLectureTypes = useCallback(() => {
    if (!eventDetails?.availableLectureTypes) {
      // Default lecture types if not specified
      return ['technical', 'marketing', 'educational', 'vip'];
    }
    
    try {
      // availableLectureTypes might be stored as JSON string
      if (typeof eventDetails.availableLectureTypes === 'string') {
        return JSON.parse(eventDetails.availableLectureTypes);
      }
      return eventDetails.availableLectureTypes;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'parse-lecture-types',
        consoleMessage: 'Error parsing available lecture types:',
        context: {
          rawValue: eventDetails?.availableLectureTypes
        }
      });
      return ['technical', 'marketing', 'educational', 'vip'];
    }
  }, [eventDetails]);

  /**
   * Check if a lecture type is available for the event
   */
  const isLectureTypeAvailable = useCallback((lectureType) => {
    const availableTypes = getAvailableLectureTypes();
    return availableTypes.includes(lectureType);
  }, [getAvailableLectureTypes]);

  /**
   * Auto-fetch event details when eventId changes
   */
  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, fetchEventDetails]);

  return {
    eventDetails,
    topics,
    topicGroups,
    isLoading,
    error,
    fetchEventDetails,
    fetchTopics,
    fetchEventTopics,
    getTopicById,
    getTopicsByIds,
    getTopicGroupById,
    searchTopics,
    getAvailableLectureTypes,
    isLectureTypeAvailable
  };
};